import { z } from "zod";
import { AD_ENTITY_STATUSES, AD_LEVELS, AD_PLATFORMS, AUDIENCE_TYPES, CAMPAIGN_OBJECTIVES, CREATIVE_TYPES, DATE_PRESETS } from "../enums";
import type { AdEntityStatus, AdLevel, AdPlatform, AudienceType, CampaignObjective, CreativeType } from "../enums";

// Media Buyer core (WP6a): request schemas shared by the API (validation) and the web app (forms), plus the DTOs the
// API returns. Money is integer minor units: insights and KPIs are converted to the client currency, budgets stay in
// the ad account currency (`budgetCurrency`).

const objectIdString = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const minorAmount = z.number().int("Montant invalide").min(0, "Montant invalide");
const boolQuery = (fallback: boolean) =>
  z
    .enum(["true", "false", "1", "0"])
    .optional()
    .transform((v) => (v === undefined ? fallback : v === "true" || v === "1"));

export const ADS_VIEW_PLATFORMS = ["meta", "tiktok", "google", "organic", "all"] as const;
export type AdsViewPlatform = (typeof ADS_VIEW_PLATFORMS)[number];

export const adsPeriodQuery = z.object({
  preset: z.enum(DATE_PRESETS).default("7d"),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type AdsPeriodQuery = z.infer<typeof adsPeriodQuery>;

export const adsOverviewQuery = adsPeriodQuery.extend({
  platform: z.enum(ADS_VIEW_PLATFORMS).default("all"),
});
export type AdsOverviewQuery = z.infer<typeof adsOverviewQuery>;

export const adsSeriesQuery = z.object({
  platform: z.enum(ADS_VIEW_PLATFORMS).default("all"),
  days: z.coerce
    .number()
    .int()
    .refine((v) => [7, 14, 30, 90].includes(v), "Période invalide")
    .default(30),
});
export type AdsSeriesQuery = z.infer<typeof adsSeriesQuery>;

const listBase = adsPeriodQuery.extend({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(120).optional(),
  platform: z.enum(AD_PLATFORMS).optional(),
  status: z.enum([...AD_ENTITY_STATUSES, "all", "delivering"]).default("all"),
  accountId: objectIdString.optional(),
  sort: z.enum(["recent", "name", "spend", "revenue", "roas", "purchases", "cpa"]).default("spend"),
});

export const campaignListQuery = listBase.extend({
  objective: z.enum(CAMPAIGN_OBJECTIVES).optional(),
});
export type CampaignListQuery = z.infer<typeof campaignListQuery>;

export const adSetListQuery = listBase.extend({ campaignId: objectIdString.optional() });
export type AdSetListQuery = z.infer<typeof adSetListQuery>;

export const adListQuery = listBase.extend({ campaignId: objectIdString.optional(), adsetId: objectIdString.optional() });
export type AdListQuery = z.infer<typeof adListQuery>;

export const entityStatusInput = z.object({
  status: z.enum(["active", "paused"]),
  reason: z.string().trim().max(300).optional().default(""),
});
export type EntityStatusInput = z.infer<typeof entityStatusInput>;

export const entityBudgetInput = z.object({
  dailyBudgetMinor: z.number().int("Montant invalide").min(1, "Le budget doit être positif"),
  reason: z.string().trim().max(300).optional().default(""),
});
export type EntityBudgetInput = z.infer<typeof entityBudgetInput>;

export const adAccountUpdateInput = z.object({ enabled: z.boolean() });

export const accountSyncInput = z.object({
  scope: z.enum(["all", "structure", "insights"]).default("all"),
  days: z.number().int().min(1).max(90).optional(),
});
export type AccountSyncInput = z.infer<typeof accountSyncInput>;

export const targetingSearchQuery = z.object({
  adAccountId: objectIdString,
  type: z.enum(["interest", "location"]),
  q: z.string().trim().min(1, "Saisissez un terme").max(100),
  country: z.string().length(2).toUpperCase().optional(),
});
export type TargetingSearchQuery = z.infer<typeof targetingSearchQuery>;

export const adLibraryQuery = z.object({
  q: z.string().trim().min(2, "Saisissez au moins 2 caractères").max(100),
  country: z.string().length(2).toUpperCase().default("TN"),
  activeStatus: z.enum(["ACTIVE", "INACTIVE", "ALL"]).default("ACTIVE"),
});
export type AdLibraryQuery = z.infer<typeof adLibraryQuery>;

export const adPreviewQuery = z.object({
  format: z
    .enum(["MOBILE_FEED_STANDARD", "DESKTOP_FEED_STANDARD", "INSTAGRAM_STANDARD", "INSTAGRAM_STORY", "INSTAGRAM_REELS", "FACEBOOK_REELS_MOBILE", "FACEBOOK_STORY_MOBILE"])
    .default("MOBILE_FEED_STANDARD"),
});

// ---------------------------------------------------------------------------------------------------------------
// Campaign wizard

export const WIZARD_OBJECTIVES = ["sales", "leads", "traffic", "awareness"] as const;
export type WizardObjective = (typeof WIZARD_OBJECTIVES)[number];

export const WIZARD_PLATFORMS = ["meta", "tiktok"] as const;
export type WizardPlatform = (typeof WIZARD_PLATFORMS)[number];

/** Conversion location on Meta ("Objectif spécifique"). */
export const META_SPECIFIC_OBJECTIVES = ["website", "shop", "messages", "calls"] as const;
export type MetaSpecificObjective = (typeof META_SPECIFIC_OBJECTIVES)[number];

export const MESSAGE_DESTINATIONS = ["whatsapp", "messenger", "instagram"] as const;
export type MessageDestination = (typeof MESSAGE_DESTINATIONS)[number];

export const CALL_TO_ACTIONS = ["SHOP_NOW", "ORDER_NOW", "BUY_NOW", "LEARN_MORE", "SIGN_UP", "CONTACT_US", "GET_OFFER", "BOOK_NOW", "SUBSCRIBE", "SEND_MESSAGE"] as const;
export type CallToAction = (typeof CALL_TO_ACTIONS)[number];

export const META_PUBLISHER_PLATFORMS = ["facebook", "instagram", "messenger", "audience_network"] as const;

export const wizardAudienceSchema = z.object({
  countries: z.array(z.string().length(2).toUpperCase()).min(1, "Choisissez au moins un pays").max(25).default(["TN"]),
  locations: z
    .array(
      z.object({
        key: z.string().trim().min(1).max(60),
        name: z.string().trim().max(160),
        type: z.enum(["region", "city"]),
        countryCode: z.string().length(2).toUpperCase().optional(),
      }),
    )
    .max(50)
    .default([]),
  ageMin: z.number().int().min(13).max(65).default(18),
  ageMax: z.number().int().min(13).max(65).default(65),
  genders: z.enum(["all", "male", "female"]).default("all"),
  interests: z
    .array(z.object({ id: z.string().trim().min(1).max(40), name: z.string().trim().max(160) }))
    .max(25)
    .default([]),
  /** Platform audience ids (externalId) included and excluded. */
  customAudienceIds: z.array(z.string().trim().max(40)).max(10).default([]),
  excludedAudienceIds: z.array(z.string().trim().max(40)).max(10).default([]),
  advantageAudience: z.boolean().default(true),
  placements: z.enum(["automatic", "manual"]).default("automatic"),
  publisherPlatforms: z.array(z.enum(META_PUBLISHER_PLATFORMS)).max(4).default([]),
});
export type WizardAudience = z.infer<typeof wizardAudienceSchema>;

export const wizardBudgetSchema = z.object({
  /** Campaign budget (Advantage campaign budget) or ad set budget. */
  level: z.enum(["campaign", "adset"]).default("adset"),
  type: z.enum(["daily", "lifetime"]).default("daily"),
  /** Entered in the client currency, converted to each ad account currency at launch. */
  amountMinor: minorAmount.nullable().default(null),
  startMode: z.enum(["now", "scheduled"]).default("now"),
  startAt: z.string().datetime({ offset: true }).nullable().default(null),
  endAt: z.string().datetime({ offset: true }).nullable().default(null),
  durationDays: z.number().int().min(1).max(365).nullable().default(7),
});
export type WizardBudget = z.infer<typeof wizardBudgetSchema>;

export const wizardCreativeSchema = z.object({
  creativeId: objectIdString.nullable().default(null),
  format: z.enum(["image", "video"]).default("image"),
  fileId: objectIdString.nullable().default(null),
  mediaUrl: z.string().url().max(1000).nullable().default(null),
  thumbnailFileId: objectIdString.nullable().default(null),
  primaryText: z.string().trim().max(2000).default(""),
  headline: z.string().trim().max(255).default(""),
  description: z.string().trim().max(255).default(""),
  callToAction: z.enum(CALL_TO_ACTIONS).default("SHOP_NOW"),
  linkUrl: z.string().trim().max(1000).default(""),
  welcomeMessage: z.string().trim().max(500).default(""),
  tiktokIdentityId: z.string().trim().max(80).nullable().default(null),
  tiktokIdentityType: z.enum(["TT_USER", "BC_AUTH_TT"]).nullable().default(null),
  tiktokIdentityBcId: z.string().trim().max(80).nullable().default(null),
});
export type WizardCreative = z.infer<typeof wizardCreativeSchema>;

export const campaignDraftSchema = z.object({
  name: z.string().trim().max(100, "100 caractères maximum").default(""),
  objective: z.enum(WIZARD_OBJECTIVES).default("sales"),
  platforms: z.array(z.enum(WIZARD_PLATFORMS)).min(1, "Choisissez une plateforme").max(2).default(["meta"]),
  accounts: z
    .object({ meta: objectIdString.nullable().default(null), tiktok: objectIdString.nullable().default(null) })
    .default({}),
  productId: objectIdString.nullable().default(null),
  productCategory: z.string().trim().max(80).default(""),
  pageId: z.string().trim().max(40).nullable().default(null),
  instagramUserId: z.string().trim().max(40).nullable().default(null),
  specificObjective: z.enum(META_SPECIFIC_OBJECTIVES).default("website"),
  messageDestination: z.enum(MESSAGE_DESTINATIONS).default("whatsapp"),
  whatsappNumber: z.string().trim().max(20).nullable().default(null),
  phoneNumber: z.string().trim().max(20).nullable().default(null),
  pixelId: z.string().trim().max(40).nullable().default(null),
  tiktokPixelId: z.string().trim().max(40).nullable().default(null),
  audience: wizardAudienceSchema.default({}),
  budget: wizardBudgetSchema.default({}),
  creative: wizardCreativeSchema.default({}),
  step: z.number().int().min(0).max(4).default(0),
});
export type CampaignDraft = z.infer<typeof campaignDraftSchema>;
export type CampaignDraftInput = z.input<typeof campaignDraftSchema>;

export const campaignTemplateInput = z.object({
  name: z.string().trim().min(2, "Nom trop court").max(80),
  draft: campaignDraftSchema,
});

export const audienceEstimateInput = z.object({
  adAccountId: objectIdString,
  audience: wizardAudienceSchema,
  objective: z.enum(WIZARD_OBJECTIVES).default("sales"),
  specificObjective: z.enum(META_SPECIFIC_OBJECTIVES).default("website"),
  pixelId: z.string().trim().max(40).nullable().optional(),
});
export type AudienceEstimateInput = z.infer<typeof audienceEstimateInput>;

export const wizardRecommendationsInput = z.object({ draft: campaignDraftSchema });

export const aiPlanInput = z.object({
  productId: objectIdString,
  dailyBudgetMinor: z.number().int().min(1, "Indiquez un budget"),
  objective: z.enum(WIZARD_OBJECTIVES).default("sales"),
  platform: z.enum(WIZARD_PLATFORMS).default("meta"),
  notes: z.string().trim().max(500).optional().default(""),
});
export type AiPlanInput = z.infer<typeof aiPlanInput>;

// ---------------------------------------------------------------------------------------------------------------
// Creatives

export const creativeInputSchema = z.object({
  name: z.string().trim().min(2, "Nom trop court").max(120),
  type: z.enum(CREATIVE_TYPES).default("image"),
  fileIds: z.array(objectIdString).max(10).default([]),
  primaryText: z.string().trim().max(2000).default(""),
  headline: z.string().trim().max(255).default(""),
  description: z.string().trim().max(255).default(""),
  callToAction: z.enum(CALL_TO_ACTIONS).default("SHOP_NOW"),
  linkUrl: z.union([z.string().trim().url("URL invalide").max(1000), z.literal("")]).default(""),
  productId: objectIdString.nullable().default(null),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  aiGenerated: z.boolean().default(false),
  status: z.enum(["draft", "ready", "archived"]).default("ready"),
});
export type CreativeInput = z.infer<typeof creativeInputSchema>;
export const creativeUpdateSchema = creativeInputSchema.partial();
export type CreativeUpdate = z.infer<typeof creativeUpdateSchema>;

export const creativeListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(24),
  q: z.string().trim().max(120).optional(),
  type: z.enum(CREATIVE_TYPES).optional(),
  status: z.enum(["draft", "ready", "archived", "all"]).default("all"),
});
export type CreativeListQuery = z.infer<typeof creativeListQuery>;

export const creativePushInput = z.object({ adAccountId: objectIdString });

// ---------------------------------------------------------------------------------------------------------------
// Audiences

export const CUSTOMER_SEGMENTS = ["delivered", "repeat", "tag", "rfm", "all_buyers"] as const;
export type CustomerSegment = (typeof CUSTOMER_SEGMENTS)[number];

export const WEBSITE_AUDIENCE_EVENTS = ["PageView", "ViewContent", "AddToCart", "InitiateCheckout", "Purchase", "Lead"] as const;
export type WebsiteAudienceEvent = (typeof WEBSITE_AUDIENCE_EVENTS)[number];

export const audienceListQuery = z.object({
  platform: z.enum(AD_PLATFORMS).optional(),
  type: z.enum(AUDIENCE_TYPES).optional(),
  q: z.string().trim().max(120).optional(),
  preset: z.enum(DATE_PRESETS).default("30d"),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type AudienceListQuery = z.infer<typeof audienceListQuery>;

export const audienceSyncInput = z.object({ adAccountId: objectIdString });

export const customerAudienceInput = z
  .object({
    adAccountId: objectIdString,
    name: z.string().trim().min(3, "Nom trop court").max(120),
    description: z.string().trim().max(300).optional().default(""),
    segment: z.enum(CUSTOMER_SEGMENTS),
    tag: z.string().trim().max(40).optional(),
    rfmSegment: z.string().trim().max(40).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.segment === "tag" && !v.tag) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["tag"], message: "Choisissez un tag" });
    if (v.segment === "rfm" && !v.rfmSegment) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rfmSegment"], message: "Choisissez un segment" });
  });
export type CustomerAudienceInput = z.infer<typeof customerAudienceInput>;

export const lookalikeAudienceInput = z.object({
  adAccountId: objectIdString,
  sourceAudienceId: objectIdString,
  country: z.string().length(2).toUpperCase().default("TN"),
  ratioPct: z.number().int().min(1).max(10).default(1),
  name: z.string().trim().max(120).optional(),
});
export type LookalikeAudienceInput = z.infer<typeof lookalikeAudienceInput>;

export const websiteAudienceInput = z.object({
  adAccountId: objectIdString,
  name: z.string().trim().min(3, "Nom trop court").max(120),
  event: z.enum(WEBSITE_AUDIENCE_EVENTS),
  retentionDays: z.number().int().min(1).max(180).default(30),
  urlContains: z.string().trim().max(200).optional().default(""),
});
export type WebsiteAudienceInput = z.infer<typeof websiteAudienceInput>;

// ---------------------------------------------------------------------------------------------------------------
// Performance

export const PERFORMANCE_BREAKDOWNS = ["age", "gender", "placement", "country"] as const;
export type PerformanceBreakdown = (typeof PERFORMANCE_BREAKDOWNS)[number];

export const performanceQuery = adsPeriodQuery.extend({
  level: z.enum(["campaign", "adset", "ad"]).default("campaign"),
  platform: z.enum(AD_PLATFORMS).optional(),
  compare: boolQuery(true),
  q: z.string().trim().max(120).optional(),
  campaignId: objectIdString.optional(),
  sort: z.enum(["spend", "revenue", "roas", "purchases", "cpa", "ctr", "impressions", "clicks"]).default("spend"),
});
export type PerformanceQuery = z.infer<typeof performanceQuery>;

export const breakdownQuery = adsPeriodQuery.extend({
  breakdown: z.enum(PERFORMANCE_BREAKDOWNS).default("age"),
  accountId: objectIdString.optional(),
  level: z.enum(AD_LEVELS).default("account"),
  entityId: objectIdString.optional(),
});
export type BreakdownQuery = z.infer<typeof breakdownQuery>;

// ---------------------------------------------------------------------------------------------------------------
// DTOs

export interface AdsMetrics {
  spendMinor: number;
  revenueMinor: number;
  impressions: number;
  reach: number;
  clicks: number;
  linkClicks: number;
  purchases: number;
  leads: number;
  addToCart: number;
  initiateCheckout: number;
  viewContent: number;
  messagingConversations: number;
  videoViews: number;
  results: number;
  roas: number | null;
  cpaMinor: number | null;
  ctr: number | null;
  cpcMinor: number | null;
  cpmMinor: number | null;
  frequency: number | null;
}

export interface FxQuoteDto {
  base: string;
  quote: string;
  rate: number | null;
  source: "identity" | "manual" | "daily" | "unavailable";
  date: string | null;
}

export interface FxInfoDto {
  currency: string;
  rates: FxQuoteDto[];
  missing: string[];
}

export interface AdAccountDto {
  id: string;
  platform: AdPlatform;
  externalId: string;
  name: string;
  currency: string;
  timezone: string;
  status: string;
  statusReason: string | null;
  businessName: string | null;
  enabled: boolean;
  lastStructureSyncAt: string | null;
  lastInsightsSyncAt: string | null;
  lastBackfillAt: string | null;
  lastError: string | null;
}

export interface AdsConnectionDto {
  platform: AdPlatform;
  connected: boolean;
  status: string | null;
  selectedAccounts: number;
  importedAccounts: number;
  lastError: string | null;
  hashedContactsEnabled: boolean;
}

export interface AdsAssetsDto {
  pages: Array<{ id: string; name: string; pictureUrl: string | null; username: string | null }>;
  instagramAccounts: Array<{ id: string; username: string; name: string; pictureUrl: string | null }>;
  pixels: Array<{ id: string; name: string }>;
  whatsappNumbers: Array<{ id: string; displayPhoneNumber: string; verifiedName: string }>;
  selectedPixelId: string | null;
  tiktokPixels: Array<{ id: string; name: string }>;
  selectedTiktokPixelId: string | null;
}

export interface AdsAccountsResponse {
  items: AdAccountDto[];
  connections: AdsConnectionDto[];
  assets: AdsAssetsDto;
  fx: FxInfoDto;
}

export interface AdsKpi {
  value: number | null;
  previous: number | null;
  delta: number | null;
  spark: number[];
}

export interface CampaignRowDto {
  id: string;
  platform: AdPlatform;
  externalId: string | null;
  adAccountId: string;
  accountName: string;
  name: string;
  objective: CampaignObjective;
  status: AdEntityStatus;
  effectiveStatus: string | null;
  dailyBudgetMinor: number | null;
  lifetimeBudgetMinor: number | null;
  budgetCurrency: string | null;
  createdVia: "platform" | "external";
  startTime: string | null;
  endTime: string | null;
  metrics: AdsMetrics;
  ordersCount: number;
  ordersRevenueMinor: number;
  updatedAt: string;
}

export interface AdSetRowDto {
  id: string;
  platform: AdPlatform;
  externalId: string | null;
  adAccountId: string;
  campaignId: string | null;
  campaignName: string;
  name: string;
  status: AdEntityStatus;
  effectiveStatus: string | null;
  optimizationGoal: string | null;
  dailyBudgetMinor: number | null;
  lifetimeBudgetMinor: number | null;
  budgetCurrency: string | null;
  targetingSummary: string;
  learningStage: string | null;
  metrics: AdsMetrics;
  /** Overview only: creative of the ad set's top spending ad, when it has an image. */
  thumbnailUrl?: string | null;
}

export interface AdRowDto {
  id: string;
  platform: AdPlatform;
  externalId: string | null;
  adAccountId: string;
  campaignId: string | null;
  campaignName: string;
  adsetId: string | null;
  adsetName: string;
  name: string;
  status: AdEntityStatus;
  effectiveStatus: string | null;
  creative: {
    title: string;
    body: string;
    imageUrl: string | null;
    thumbnailUrl: string | null;
    videoId: string | null;
    callToAction: string | null;
    linkUrl: string | null;
    format: string | null;
  };
  previewUrl: string | null;
  reviewFeedback: unknown;
  metrics: AdsMetrics;
}

export interface AudienceRowDto {
  id: string;
  platform: AdPlatform;
  adAccountId: string;
  externalId: string | null;
  name: string;
  type: AudienceType;
  subtype: string | null;
  description: string;
  sizeLower: number | null;
  sizeUpper: number | null;
  status: string | null;
  origin: "platform" | "digisubs";
  segment: string | null;
  lastUploadAt: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  usedByAdSets: number;
  metrics: AdsMetrics;
}

export interface FunnelStepDto {
  event: "view_content" | "add_to_cart" | "initiate_checkout" | "purchase" | "lead";
  count: number;
  previous: number;
  rate: number | null;
  delta: number | null;
}

export interface AdsOverviewDto {
  currency: string;
  platform: AdsViewPlatform;
  period: { from: string; to: string; previousFrom: string; previousTo: string; days: number };
  accounts: { total: number; byPlatform: Record<AdPlatform, number>; lastSyncAt: string | null; errors: number };
  kpis: {
    spend: AdsKpi;
    revenue: AdsKpi;
    roas: AdsKpi;
    purchases: AdsKpi;
    cpa: AdsKpi;
    ordersRevenue: AdsKpi;
    deliveredRevenue: AdsKpi;
    orders: AdsKpi;
    realRoas: AdsKpi;
  };
  series: Array<{ date: string; spendMinor: number; revenueMinor: number; purchases: number }>;
  spendByPlatform: Array<{ platform: AdPlatform; spendMinor: number }>;
  objectives: {
    all: Array<{ objective: CampaignObjective; spendMinor: number; campaigns: number }>;
    active: Array<{ objective: CampaignObjective; spendMinor: number; campaigns: number }>;
  };
  recentCampaigns: CampaignRowDto[];
  topAdSets: AdSetRowDto[];
  topAds: AdRowDto[];
  audiences: AudienceRowDto[];
  funnel: FunnelStepDto[];
  organic: {
    revenue: AdsKpi;
    orders: AdsKpi;
    averageOrder: AdsKpi;
    deliveredRevenue: AdsKpi;
    deliveryRate: AdsKpi;
    series: Array<{ date: string; revenueMinor: number; orders: number }>;
    bySource: Array<{ source: string; orders: number; revenueMinor: number }>;
  } | null;
  fx: FxInfoDto;
}

export interface AdsSeriesDto {
  currency: string;
  points: Array<{ date: string; spendMinor: number; revenueMinor: number; purchases: number }>;
  fx: FxInfoDto;
}

export interface DecisionRowDto {
  id: string;
  action: string;
  level: AdLevel | null;
  entityName: string;
  entityExternalId: string | null;
  outcome: "success" | "failed";
  reason: string;
  error: string | null;
  actorName: string | null;
  before: unknown;
  after: unknown;
  createdAt: string;
}

export interface CampaignDetailDto {
  campaign: CampaignRowDto & {
    rawObjective: string | null;
    bidStrategy: string | null;
    specialAdCategories: string[];
    lastSyncedAt: string | null;
    issues: unknown[];
    draftId: string | null;
  };
  previous: AdsMetrics;
  series: Array<{ date: string; spendMinor: number; revenueMinor: number; purchases: number; clicks: number; impressions: number }>;
  adSets: AdSetRowDto[];
  ads: AdRowDto[];
  decisions: DecisionRowDto[];
  canDuplicate: boolean;
  currency: string;
  fx: FxInfoDto;
}

export interface ExecutionResultDto {
  status: "executed" | "pending_approval";
  recommendationId: string | null;
  message: string;
}

export interface CampaignDraftDto {
  id: string;
  name: string;
  platform: AdPlatform;
  draft: CampaignDraft;
  issues: Array<{ code: string; message: string }>;
  launch: {
    at: string | null;
    results: Array<{ platform: WizardPlatform; ok: boolean; campaignId: string | null; externalId: string | null; error: string | null }>;
  } | null;
  updatedAt: string;
  createdAt: string;
}

export interface DraftValidationDto {
  valid: boolean;
  issues: Array<{ field: string; message: string; severity: "error" | "warning" }>;
  budgets: Array<{ platform: WizardPlatform; currency: string; amountMinor: number | null; rate: number | null }>;
}

export interface CampaignTemplateDto {
  id: string;
  name: string;
  draft: CampaignDraft;
  createdAt: string;
}

export interface AudienceEstimateDto {
  available: boolean;
  reason: string | null;
  lower: number | null;
  upper: number | null;
  ready: boolean;
  genderSplit: { male: number; female: number } | null;
}

export interface WizardRecommendationsDto {
  source: "ai" | "rules";
  reason: string | null;
  items: Array<{ text: string; kind: "budget" | "audience" | "creative" | "tracking" | "general" }>;
}

export interface AiPlanDto {
  draft: CampaignDraft;
  rationale: string[];
  unresolvedInterests: string[];
}

export interface CreativeDto {
  id: string;
  name: string;
  type: CreativeType;
  files: Array<{ fileId: string | null; url: string; kind: "image" | "video"; width: number | null; height: number | null; pushed: { meta: boolean; tiktok: boolean } }>;
  primaryText: string;
  headline: string;
  description: string;
  callToAction: string;
  linkUrl: string;
  productId: string | null;
  tags: string[];
  aiGenerated: boolean;
  status: "draft" | "ready" | "archived";
  usedInAds: number;
  /** Last 30 days of the ads using the creative, in the ad account currency (`currency`). */
  performance: { spendMinor: number; revenueMinor: number; purchases: number; ctr: number | null; roas: number | null; currency: string | null };
  createdAt: string;
  updatedAt: string;
}

export interface CustomerSegmentsDto {
  delivered: number;
  repeat: number;
  allBuyers: number;
  withContact: number;
  tags: Array<{ tag: string; count: number }>;
  rfm: Array<{ segment: string; count: number }>;
  hashedContactsEnabled: { meta: boolean; tiktok: boolean };
}

export interface PerformanceRowDto {
  id: string;
  level: "campaign" | "adset" | "ad";
  platform: AdPlatform;
  name: string;
  parentName: string;
  status: AdEntityStatus;
  metrics: AdsMetrics;
  previous: AdsMetrics | null;
}

export interface PerformanceDto {
  currency: string;
  period: { from: string; to: string; previousFrom: string; previousTo: string; days: number };
  totals: AdsMetrics;
  previousTotals: AdsMetrics | null;
  rows: PerformanceRowDto[];
  fx: FxInfoDto;
}

export interface BreakdownDto {
  available: boolean;
  reason: string | null;
  breakdown: PerformanceBreakdown;
  currency: string;
  rows: Array<{ key: string; label: string; spendMinor: number; impressions: number; clicks: number; purchases: number; revenueMinor: number; roas: number | null; ctr: number | null }>;
}

export interface TargetingOptionDto {
  id: string;
  name: string;
  type: string;
  description: string | null;
  sizeLower: number | null;
  sizeUpper: number | null;
  countryCode: string | null;
}

export interface TikTokIdentityDto {
  id: string;
  type: "TT_USER" | "BC_AUTH_TT" | "AUTH_CODE" | "CUSTOMIZED_USER";
  displayName: string;
  username: string | null;
  pictureUrl: string | null;
  bcId: string | null;
  canPush: boolean;
}

export interface AdLibraryItemDto {
  id: string;
  pageId: string | null;
  pageName: string;
  startedAt: string | null;
  bodies: string[];
  titles: string[];
  snapshotUrl: string | null;
  platforms: string[];
}
